import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    Page,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    Permission,
    StaffPosition,
    StaffPositionViewResource,
} from "generated/openApi";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StaffPositionPageHeader } from "./StaffPositionPageHeader";
import { StaffPositionPageBody } from "./StaffPositionPageBody";

const routeName = "staffPosition";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const staffPositionPageRoute: PageRoute<Params, OptionalParams> = {
    handle: "Staff Position",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export const StaffPositionPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params } = useParams(routeName, Params, OptionalParams);

    const [staffPosition, setStaffPosition] = useState<StaffPosition>({} as StaffPosition);
    const [initStaffPosition, setInitStaffPosition] = useState<StaffPosition>(staffPosition);
    const [viewResource, setViewResource] = useState<StaffPositionViewResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [staffPosition] });
    const { validationErrors, setValidationErrors } = useValidationErrors();

    useEffect(() => {
        fetch(params.id);

        ApiService.staffPositions.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${staffPositionPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        ApiService.staffPositions.get(id).then((response) => {
            setStaffPosition(response);
            setInitStaffPosition(response);
            resetUnsavedChanges([response]);
        });
    };

    const pageHeader = (
        <StaffPositionPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={staffPositionPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            staffPosition={staffPosition}
            setStaffPosition={setStaffPosition}
            initStaffPosition={initStaffPosition}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
        />
    );

    const pageBody = (
        <StaffPositionPageBody
            staffPosition={staffPosition}
            setStaffPosition={setStaffPosition}
            viewResource={viewResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};
